import React from "react"
import { graphql } from "gatsby"
import { withTheme } from "emotion-theming"
import Img from "gatsby-image"
import { Flex } from "@rebass/grid/emotion"

import Layout from "@layouts/base"

import Circle from "@components/circle"
import { Container, Row, Col } from "@components/grid"
import {
  Collapse,
  CollapseItem,
  CollapseHeading,
  CollapseButton,
  CollapsePanel,
} from "@components/collapse"
import Infographic from "@components/infographic"
import { KeylineDash } from "@components/keyline"
import Panel, { ImagePanel } from "@components/panel"
import Player from "@components/player"
import { Caption, Heading, Pullquote } from "@components/type"

import { rhythm } from "@utils/typography"

import { Carrot, Clock, Megaphone, People, Utensils } from "@svgs"

import krista from "@audio/krista.m4a"
import amy from "@audio/amy.m4a"

const Family = ({ theme, data }) => {
  const pageTitle = "Hamilton Community Food Centre"
  const borderRight = `3px dashed ${theme.colours.secondary}`
  return (
    <Layout image={data.hero.childImageSharp.fluid} title={pageTitle}>
      <Container>
        <Heading colour={theme.colours.primary}>{pageTitle}</Heading>
        <Player title="Krista & Shane – Community Food Centre Podcast" src={krista} />
        <Player title="Amy & Shane – Community Food Centre Podcast" src={amy} />
        <h2>2018 Impact Summary</h2>
        <p>
          Hamilton Community Food Centre offers food access, food skills and
          education and engagement programs in a dignified and welcoming space
          that brings the community together to grow, cook, share, and advocate
          for good food.
        </p>

        <p>
          This Impact Summary provides a snapshot of our 2018 program data, as
          well as the results from our 2018 Annual Program Survey. The survey
          was conducted as an in-person interview with 75 adult participants
          across a variety of our programs, and helps us to better understand
          outcomes in the areas of healthy food access; knowledge, skills and
          behaviours; social inclusion and civic engagement.
        </p>
        <Collapse allowZeroExpanded={true}>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Food access programs
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel>
              <ul>
                <li>Seniors’ Wellness Morning and Breakfast</li>
                <li>Global Roots Lunch</li>
                <li>Family Dinner</li>
                <li>Good Food Market and Café</li>
              </ul>
            </CollapsePanel>
          </CollapseItem>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Food skills programs
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel>
              <ul>
                <li>After school program</li>
                <li>Intercultural Community Kitchen</li>
                <li>Middle East Community Kitchen</li>
                <li>Make and Take Meals</li>
                <li>Good Food Market and Café</li>
                <li>Self-care Club</li>
                <li>Welcome Baby</li>
                <li>Garden Club</li>
                <li>Summer Kids Club</li>
              </ul>
            </CollapsePanel>
          </CollapseItem>
          <CollapseItem>
            <CollapseHeading>
              <CollapseButton background={theme.colours.primary}>
                Education and engagement programs
              </CollapseButton>
            </CollapseHeading>
            <CollapsePanel>
              <ul>
                <li>Community Advocacy Office</li>
                <li>Community Action Training</li>
                <li>Cooking Up Justice</li>
                <li>Women's Self-care Group</li>
                <li>Social Justice Campaigns and Events</li>
                <li>Seniors' Skill Share Circle</li>
                <li>Hamilton Community Legal Clinic</li>
              </ul>
            </CollapsePanel>
          </CollapseItem>
        </Collapse>
      </Container>
      <ImagePanel image={data.testimonial.childImageSharp.fluid}>
        <Container>
          <Row>
            <Col width={[1, 1, 2 / 3]}>
              <Panel background={theme.colours.primary} padding={1}>
                <Caption style={{ lineHeight: rhythm(1.2), marginBottom: 0 }}>
                  "I feel like I have my second home here. I’ve met a lot of
                  people from other countries and we learn about healthy food
                  from these countries–which is very important and special. I
                  feel very happy here."
                </Caption>
              </Panel>
            </Col>
          </Row>
        </Container>
      </ImagePanel>
      <Panel>
        <Row>
          <Col width={1 / 2} style={{ borderRight }}>
            <h3>Increasing access to healthy food</h3>
            <Infographic
              icon={<Carrot />}
              value="11,937"
              colour={theme.colours.secondary}
            >
              Healthy Meals Served and Sent Home
            </Infographic>
          </Col>
          <Col width={1 / 2}>
            <h3>Building healthy food skills and behaviours</h3>
            <Infographic
              icon={<Utensils />}
              value="218"
              colour={theme.colours.danger}
            >
              Food Skills Sessions Offered
            </Infographic>
          </Col>
        </Row>
        <Row>
          <Col width={1 / 2} style={{ borderRight }}>
            <Infographic
              icon={
                <Circle
                  value={88}
                  text="88%"
                  colour={theme.colours.secondary}
                />
              }
              lg={true}
              colour={theme.colours.secondary}
            >
              of people surveyed say our programs provide an important source of
              healthy food
            </Infographic>
          </Col>
          <Col width={1 / 2}>
            <Infographic
              icon={
                <Circle value={81} text="81%" colour={theme.colours.danger} />
              }
              lg={true}
              colour={theme.colours.danger}
            >
              say they're eating more fruits and vegetables
            </Infographic>
          </Col>
        </Row>
        <KeylineDash />
        <Row>
          <Col width={1 / 2} alignItems="center" style={{ borderRight }}>
            <h3>Improving health</h3>
            <Flex alignItems="center" flex="1" style={{ height: "100%" }}>
              <Caption colour={theme.colours.primary}>
                "When your health falls into place, you're a happier person."
              </Caption>
            </Flex>
          </Col>
          <Col width={1 / 2}>
            <h3>Reducing social isolation and increasing support</h3>
            <Infographic
              icon={<Megaphone />}
              value="1,320"
              colour={theme.colours.warning}
            >
              visits to the Advcacy Office for Support
            </Infographic>
          </Col>
        </Row>
        <Row>
          <Col width={1 / 2} style={{ borderRight }}>
            <Infographic
              icon={<Circle value={74} text="74%" />}
              lg={true}
              colour={theme.colours.success}
            >
              say our programs have contributed to positive changes in their
              mental health
            </Infographic>
          </Col>
          <Col width={1 / 2}>
            <Infographic
              icon={<Circle value={95} text="95%" />}
              lg={true}
              colour={theme.colours.warning}
            >
              feel they belong to a community at The Hamilton CFC
            </Infographic>
          </Col>
        </Row>
        <KeylineDash />
        <Row>
          <Col width={1 / 2} style={{ borderRight }}>
            <Infographic
              icon={<Clock />}
              value="3,948"
              colour={theme.colours.primary}
            >
              Volunteer Hours Contributed
            </Infographic>
          </Col>
          <Col width={1 / 2}>
            <Infographic
              icon={<People />}
              value="74%"
              colour={theme.colours.primary}
            >
              of people have become more involved in their community because of
              something they learned at here
            </Infographic>
          </Col>
        </Row>
      </Panel>
      <Panel>
        <Caption colour={theme.colours.primary} scale={2 / 3}>
          "After completing the Community Action Training I became more involved
          and can help my community more."
        </Caption>
      </Panel>
      <Container>
        <Row>
          <Col width={1 / 2}>
            <Img fluid={data.people.childImageSharp.fluid} alt="" />
          </Col>
          <Col width={1 / 2}>
            <Img fluid={data.grapes.childImageSharp.fluid} alt="" />
          </Col>
        </Row>
        <Row>
          <Col width={1 / 2}>
            <Img fluid={data.tomatoes.childImageSharp.fluid} alt="" />
          </Col>
          <Col width={1 / 2}>
            <Img fluid={data.cooking.childImageSharp.fluid} alt="" />
          </Col>
        </Row>
      </Container>
      <Panel>
        <Caption>
          What’s the most important difference Hamilton CFC makes?
        </Caption>
        <Pullquote colour={theme.colours.primary}>
          68% of our members told us it's COMMUNITY CONNECTION.
        </Pullquote>
        <Caption>
          Hamilton CFC is one of ten Community Food Centres across Canada that
          are bringing people together to grow, cook, share, and advocate for
          good food for all in partnership with Community Food Centres Canada.
        </Caption>
        <Img fixed={data.cfcc.childImageSharp.fixed} alt="" />
      </Panel>
    </Layout>
  )
}

export const query = graphql`
  query {
    hero: file(relativePath: { eq: "hero/community.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 100) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    testimonial: file(relativePath: { eq: "community/testimonial.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 100) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    people: file(relativePath: { eq: "community/people.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 500, quality: 100) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    grapes: file(relativePath: { eq: "community/grapes.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 500, quality: 100) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    tomatoes: file(relativePath: { eq: "community/tomatoes.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 500, quality: 100) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    cooking: file(relativePath: { eq: "community/cooking.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 500, quality: 100) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    cfcc: file(relativePath: { eq: "community/CFCC.png" }) {
      childImageSharp {
        fixed(width: 250, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`
export default withTheme(Family)
